import {
  ApiBlueprint,
  configApiRef,
  createFrontendModule,
  createTranslationMessages,
} from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  ThemeBlueprint,
  TranslationBlueprint,
} from '@backstage/plugin-app-react';
import { catalogTranslationRef } from '@backstage/plugin-catalog';
import { catalogReactTranslationRef } from '@backstage/plugin-catalog-react';
import {
  ScmAuth,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  iconMoon,
  iconSun,
  nsDarkTheme,
  Icon,
  nsLightTheme,
} from '@ns/plugin-ns-theme';
import { SignInPageComponent } from './SignInPage';

const scmIntegrationsApi = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams({
      api: scmIntegrationsApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
});

const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => SignInPageComponent,
  },
});

// name 'light' / 'dark' makes these extensions resolve to the built-in IDs
// theme:app/light and theme:app/dark, so the NS themes OVERRIDE the Backstage
// defaults instead of being offered alongside them. This removes the need for
// `theme:app/*: false` config-disabling (which is fragile because env configs
// replace the app.extensions array rather than merging it).
const nsLightThemeExt = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'ns-light',
      title: 'Light',
      variant: 'light',
      icon: <Icon glyph={iconSun} aria-label="NS Light theme" />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={nsLightTheme} children={children} />
      ),
    },
  },
});

const nsDarkThemeExt = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'ns-dark',
      title: 'Dark',
      variant: 'dark',
      icon: <Icon glyph={iconMoon} aria-label="NS Dark theme" />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={nsDarkTheme} children={children} />
      ),
    },
  },
});

// The Catalog overview header reads indexPage.title from the translation API,
// not from the page's config.title, so it needs its own override.
const catalogTranslation = TranslationBlueprint.make({
  name: 'catalog-index-title',
  params: {
    resource: createTranslationMessages({
      ref: catalogTranslationRef,
      full: false,
      messages: { 'indexPage.title': 'Full catalog' },
    }),
  },
});

// The design calls the starred list "Favorite"; the other UserListPicker
// labels already match upstream.
const catalogReactTranslation = TranslationBlueprint.make({
  name: 'catalog-react-user-list-picker',
  params: {
    resource: createTranslationMessages({
      ref: catalogReactTranslationRef,
      full: false,
      messages: { 'userListPicker.personalFilter.starredLabel': 'Favorite' },
    }),
  },
});

// Search uses the native page:search route. Do not register a second page at /search.

export const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    scmIntegrationsApi,
    scmAuthApi,
    signInPage,
    nsLightThemeExt,
    nsDarkThemeExt,
    catalogTranslation,
    catalogReactTranslation,
  ],
});
